import { useState } from "react";
import { ChevronRight, X } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintContent, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import type { ZplPrintDocument } from "@/lib/zplPrintLocation";
import { cn } from "@/lib/utils";
import { pottingCartonMapKey, type PottingCartonSendFlow } from "./pottingCartonKey";

export { pottingCartonMapKey, type PottingCartonSendFlow } from "./pottingCartonKey";

export interface PottingCartonCardModel {
  cartonNumber: number;
  laborSku: string;
  productLabel: string;
  sealed: boolean;
  sealedAt?: string | null;
  labelPrintedAt?: string | null;
  rowCount: number;
  flow?: PottingCartonSendFlow;
}
export function pottingCartonPrintLabelButtonText(carton: Pick<PottingCartonCardModel, "labelPrintedAt">): string {
  return carton.labelPrintedAt != null && carton.labelPrintedAt.trim() !== "" ? "Reprint label" : "Print label";
}

export interface PottingCartonDraftUnit {
  id: number;
  serial: string | null;
  finishedSku: string | null;
  type: "FI-3C" | "FI-5A Chinese" | "FI-5A USA";
}

function draftUnitSerialLabel(unit: PottingCartonDraftUnit): string {
  if (unit.serial != null && unit.serial.trim() !== "") {
    return unit.serial;
  }
  if (unit.type === "FI-5A USA") {
    return "USA shell";
  }
  return "—";
}

function draftUnitFinishedSkuLabel(unit: PottingCartonDraftUnit): string {
  if (unit.finishedSku != null && unit.finishedSku.trim() !== "") {
    return unit.finishedSku;
  }
  if (unit.type === "FI-5A USA") {
    return "Unserialized";
  }
  return "—";
}

export function PottingOperatorCartonsPanel({
  cartons,
  writesAllowed,
  busy,
  highlightedCartonKey,
  flashingCartonKey,
  flashingCartonTone = "success",
  draftUnitsByCarton,
  onRemoveDraftUnit,
  removePendingDraftId,
  onSeal,
  onPrint,
  sealPendingCartonKey,
  printPendingCartonKey,
  allowSeal = true,
  allowPrint = true,
  allowRemoveDraftUnits = false,
  layout = "columns",
  station,
  onStationChange,
  documents = [],
  unavailableMessage,
  readOnlyHoverHint = "Your role has read-only access to the Manufacturing hub module.",
}: {
  cartons: PottingCartonCardModel[];
  writesAllowed: boolean;
  busy: boolean;
  highlightedCartonKey?: string | null;
  /** Brief green pulse when a scan lands in this carton (Pre-Encapsulation). */
  flashingCartonKey?: string | null;
  flashingCartonTone?: "success" | "warning" | null;
  /** Draft rows grouped by flow + carton number (Pre-Encapsulation unit list). */
  draftUnitsByCarton?: Record<string, PottingCartonDraftUnit[]>;
  onRemoveDraftUnit?: (id: number) => void;
  removePendingDraftId?: number | null;
  onSeal: (carton: Pick<PottingCartonCardModel, "cartonNumber" | "flow">) => void;
  onPrint: (carton: Pick<PottingCartonCardModel, "cartonNumber" | "flow">) => void;
  sealPendingCartonKey?: string | null;
  printPendingCartonKey?: string | null;
  allowSeal?: boolean;
  allowPrint?: boolean;
  allowRemoveDraftUnits?: boolean;
  /** `columns`: one column per carton across full width; `stack`: vertical list (supervisor review). */
  layout?: "columns" | "stack";
  /** When set, shows a ZPL printer picker (always enabled — not a hub write). */
  zplPrinter?: string;
  station?: string;
  onStationChange?: (station: string) => void;
  documents?: readonly ZplPrintDocument[];
  unavailableMessage?: string | null;
  readOnlyHoverHint?: string;
}) {
  const [expandedCartons, setExpandedCartons] = useState<Record<string, boolean>>({});
  const visible = cartons;
  const showUnitLists = draftUnitsByCarton != null;

  function toggleCartonExpanded(cartonKey: string) {
    setExpandedCartons((previous) => ({
      ...previous,
      [cartonKey]: !previous[cartonKey],
    }));
  }

  return (
    <Card className={layout === "stack" ? "h-fit" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Your cartons
          <HoverHintWithGlyph
            hoverHint="Each carton holds one product type (same Megaventory labor SKU and description). Scans auto-route to your open box for that SKU. When a box is full, seal it, print its label, then it leaves this list."
          />
        </CardTitle>
        <HoverHintContent hoverHint="Seal before printing. After a successful print, the carton is hidden here; new scans for the same product open a new carton number.">
          <p className="text-sm text-slate-500">
            {visible.length === 0
              ? "No cartons yet — add a device or clamp to the draft to start a box."
              : `${visible.length} carton${visible.length === 1 ? "" : "s"} on your open send draft.`}
          </p>
        </HoverHintContent>
      </CardHeader>
      <CardBody className={layout === "columns" ? "overflow-x-auto" : "space-y-3"}>
        {onStationChange != null ? (
          <ZplLocationSelect
            id="potting-operator-location"
            value={station ?? ""}
            onChange={onStationChange}
            unavailableMessage={unavailableMessage}
            className="mb-4 max-w-xs"
          
      documents={documents}
    />
        ) : null}
        {visible.length === 0 ? (
          <p className="text-sm text-slate-500">Scan into the potting draft to create cartons.</p>
        ) : (
          <div
            className={cn(
              layout === "columns"
                ? "grid gap-4"
                : "space-y-3",
            )}
            style={
              layout === "columns"
                ? { gridTemplateColumns: "repeat(auto-fill, minmax(14rem, 16rem))" }
                : undefined
            }
          >
          {visible.map((carton) => {
            const cartonKey = pottingCartonMapKey(carton.flow, carton.cartonNumber);
            const highlighted = highlightedCartonKey === cartonKey;
            const flashing = flashingCartonKey === cartonKey;
            const isSealing = sealPendingCartonKey === cartonKey;
            const isPrinting = printPendingCartonKey === cartonKey;
            const units = draftUnitsByCarton?.[cartonKey] ?? [];
            const canExpandUnits = showUnitLists && units.length > 0;
            const expanded = expandedCartons[cartonKey] === true;
            return (
              <div
                key={cartonKey}
                className={cn(
                  "rounded-lg border px-3 py-3",
                  flashing
                    ? flashingCartonTone === "warning"
                      ? "animate-carton-warning-flash"
                      : "animate-carton-success-flash"
                    : highlighted
                      ? "border-brand-400 bg-brand-50/40"
                      : "border-slate-200 bg-white",
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900">Carton {carton.cartonNumber}</p>
                      {carton.flow === "rework" ? <Badge tone="info">Rework</Badge> : null}
                    </div>
                    <p className="mt-0.5 font-mono text-xs text-slate-700">
                      {carton.laborSku} · {carton.productLabel}
                    </p>
                    <div className="mt-1 flex items-center gap-1">
                      {canExpandUnits ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 rounded px-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          aria-expanded={expanded}
                          aria-label={
                            expanded
                              ? `Hide ${carton.rowCount} unit${carton.rowCount === 1 ? "" : "s"} in carton ${carton.cartonNumber}`
                              : `Show ${carton.rowCount} unit${carton.rowCount === 1 ? "" : "s"} in carton ${carton.cartonNumber}`
                          }
                          onClick={() => toggleCartonExpanded(cartonKey)}
                        >
                          <ChevronRight
                            className={cn(
                              "h-3.5 w-3.5 shrink-0 transition-transform",
                              expanded ? "rotate-90" : "",
                            )}
                            aria-hidden
                          />
                          <span>
                            {carton.rowCount} unit{carton.rowCount === 1 ? "" : "s"}
                          </span>
                        </button>
                      ) : (
                        <p className="text-xs text-slate-500">
                          {carton.rowCount} unit{carton.rowCount === 1 ? "" : "s"}
                        </p>
                      )}
                    </div>
                  </div>
                  {carton.sealed ? (
                    <Badge tone="success">Sealed</Badge>
                  ) : (
                    <Badge tone="info">In progress</Badge>
                  )}
                </div>
                {canExpandUnits && expanded ? (
                  <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
                    {units.map((unit) => {
                      const removing = removePendingDraftId === unit.id;
                      const removeEnabled =
                        allowRemoveDraftUnits &&
                        carton.flow !== "rework" &&
                        !carton.sealed &&
                        writesAllowed &&
                        !busy &&
                        onRemoveDraftUnit != null;
                      return (
                        <li
                          key={unit.id}
                          className="flex items-start gap-2 rounded-md bg-slate-50 px-2 py-1.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-mono text-xs text-slate-900">
                              {draftUnitSerialLabel(unit)}
                            </p>
                            <p className="truncate font-mono text-[11px] text-slate-500">
                              {draftUnitFinishedSkuLabel(unit)}
                            </p>
                          </div>
                          {allowRemoveDraftUnits ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 shrink-0 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              disabled={!removeEnabled}
                              isLoading={removing}
                              aria-label={`Remove ${draftUnitSerialLabel(unit)} from carton ${carton.cartonNumber}`}
                              onClick={() => onRemoveDraftUnit?.(unit.id)}
                            >
                              {!removing ? <X className="h-4 w-4" aria-hidden /> : null}
                            </Button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  {allowSeal && !carton.sealed ? (
                    <ReadOnlyHoverLayer
                      active={!writesAllowed}
                      hoverHint={readOnlyHoverHint}
                      layout="inline"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        disabled={!writesAllowed || busy || carton.rowCount < 1}
                        isLoading={isSealing}
                        onClick={() => onSeal(carton)}
                      >
                        Seal carton
                      </Button>
                    </ReadOnlyHoverLayer>
                  ) : null}
                  {allowPrint && carton.sealed ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy || carton.rowCount < 1}
                      isLoading={isPrinting}
                      onClick={() => onPrint(carton)}
                    >
                      {pottingCartonPrintLabelButtonText(carton)}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
